import { useMemo } from "react";
import type { ShiftType } from "@utils/grade-util";
import type { GradeShiftGridRowDisplay } from "./GradeShiftConfigModal";
import type { TeamsSchema } from "@/hooks/roster-craete/useTeamsQuery";
import { sortTeamsByName } from "@/utils/team-sort-util";
import {
  ShiftRequirementDialog,
  type ShiftRequirementColumn,
} from "./ShiftRequirementDialog";

export type TeamShiftCounts = Partial<Record<ShiftType, number>>;

type TeamShiftConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  teams: TeamsSchema;
  memberCountByTeam: Record<number, number>;
  shiftSettings: Record<number, TeamShiftCounts>;
  shiftDisplayInfo: GradeShiftGridRowDisplay[];
  totalStaffCount: number;
  teamConfiguredTotal: number;
  unassignedCount?: number;
  isInvalidTeam: (teamId: number) => boolean;
  onMinStaffChange: (teamId: number, shift: ShiftType, value: string) => void;
  onRenameTeam?: (teamId: number, currentName: string) => void;
  onDeleteTeam?: (teamId: number) => void;
  onAddTeam?: () => void;
};

export default function TeamShiftConfigModal({
  isOpen,
  onClose,
  onConfirm,
  teams,
  memberCountByTeam,
  shiftSettings,
  shiftDisplayInfo,
  totalStaffCount,
  teamConfiguredTotal,
  unassignedCount = 0,
  isInvalidTeam,
  onMinStaffChange,
  onRenameTeam,
  onDeleteTeam,
  onAddTeam,
}: TeamShiftConfigModalProps) {
  const displayTeams = useMemo(() => sortTeamsByName(teams), [teams]);

  const columns = useMemo<ShiftRequirementColumn[]>(
    () =>
      displayTeams.map((team) => ({
        id: String(team.team_id),
        label: team.team_name,
        count: memberCountByTeam[team.team_id] ?? 0,
        isInvalid: isInvalidTeam(team.team_id),
        canRename: Boolean(onRenameTeam),
        canDelete: Boolean(onDeleteTeam),
      })),
    [displayTeams, isInvalidTeam, memberCountByTeam, onDeleteTeam, onRenameTeam]
  );

  return (
    <ShiftRequirementDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Team Shift 인원 설정"
      description="Team별로 Shift 최소 인원을 설정합니다. 팀 구성과 최소 인원 조건은 기존 저장 방식 그대로 반영됩니다."
      configuredLabel="Team 설정 합계"
      addButtonLabel="Team 추가"
      columns={columns}
      rows={shiftDisplayInfo}
      totalStaffCount={totalStaffCount}
      configuredTotal={teamConfiguredTotal}
      unassignedCount={unassignedCount}
      getValue={(teamId, shift) => shiftSettings[Number(teamId)]?.[shift] ?? 0}
      onValueChange={(teamId, shift, value) =>
        onMinStaffChange(Number(teamId), shift, value)
      }
      onRename={(teamId, currentLabel) =>
        onRenameTeam?.(Number(teamId), currentLabel)
      }
      onDelete={(teamId) => onDeleteTeam?.(Number(teamId))}
      onAdd={onAddTeam}
    />
  );
}
